import React from 'react';
import { Subcontractor, SubcontractType } from '../../../types';
import { KlFullModal } from '../modal/KlFullModal';
import { cleanRut, formatRut } from '../../../utils/chileData';

export interface MobileCreateSubcontractorModalProps {
    open: boolean;
    onClose: () => void;
    editingSubcontractorId: string | null;
    newSubcontractor: Omit<Subcontractor, 'id'>;
    setNewSubcontractor: React.Dispatch<React.SetStateAction<Omit<Subcontractor, 'id'>>>;
    subcontractTypes: SubcontractType[];
    globalSubcontractors?: Subcontractor[];
    subcontractors: Subcontractor[];
    onSave: () => void;
}

export const MobileCreateSubcontractorModal: React.FC<MobileCreateSubcontractorModalProps> = ({
    open,
    onClose,
    editingSubcontractorId,
    newSubcontractor,
    setNewSubcontractor,
    subcontractTypes,
    globalSubcontractors,
    subcontractors,
    onSave
}) => {
    const cleanCurrentRut = cleanRut(newSubcontractor.rut);
    const isRutEntered = cleanCurrentRut.length >= 2;
    const isRutFound = !editingSubcontractorId && isRutEntered && (
        (globalSubcontractors || []).some(s => cleanRut(s.rut) === cleanCurrentRut) ||
        subcontractors.some(s => cleanRut(s.rut) === cleanCurrentRut)
    );

    const isFormValid = Boolean(newSubcontractor.rut && newSubcontractor.razonSocial);

    const handleRutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatRut(e.target.value);
        const cleaned = cleanRut(formatted);

        if (!editingSubcontractorId && cleaned.length >= 8) {
            const match = (globalSubcontractors || []).find(s => cleanRut(s.rut) === cleaned) ||
                          subcontractors.find(s => cleanRut(s.rut) === cleaned);
            if (match) {
                setNewSubcontractor({
                    rut: formatted,
                    razonSocial: match.razonSocial,
                    giro: match.giro || '',
                    representante: match.representante || '',
                    telefono: match.telefono || '',
                    correo: match.correo || '',
                    subcontractTypeId: match.subcontractTypeId || '',
                    fono: match.fono || '',
                    ciudad: match.ciudad || '',
                    comuna: match.comuna || '',
                    status: match.status || 'Activo',
                    linkedUserIds: match.linkedUserIds || []
                });
                return;
            }
        }

        setNewSubcontractor(prev => ({ ...prev, rut: formatted }));
    };

    return (
        <KlFullModal
            open={open}
            onClose={onClose}
            title={editingSubcontractorId ? 'Editar empresa contratista' : 'Nueva empresa contratista'}
            footer={
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-[0_0_36%] h-11 rounded-[12px] font-bold text-[14px] border border-[#CBD5E1] dark:border-slate-700 bg-white dark:bg-slate-800 text-[#334155] dark:text-slate-200 active:scale-[0.98] transition-transform"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={!isFormValid}
                        className={`flex-1 h-11 rounded-[12px] font-bold text-[14px] text-white transition-all active:scale-[0.98] ${
                            isFormValid
                                ? 'bg-[#2563EB] hover:bg-blue-600 shadow-md'
                                : 'bg-[#E2E8F0] dark:bg-slate-800 text-[#64748B] dark:text-slate-500 cursor-not-allowed'
                        }`}
                    >
                        Guardar empresa
                    </button>
                </div>
            }
        >
            <div className="flex flex-col gap-2.5 pb-6">
                {/* TARJETA 1: IDENTIFICACIÓN (09-crear-empresa-contratista.html) */}
                <div className="bg-white dark:bg-slate-900 rounded-[14px] p-3 shadow-xs border border-slate-100 dark:border-slate-800 flex flex-col gap-2.5">
                    <div className="text-[13px] font-bold text-[#0F172A] dark:text-slate-100">
                        Identificación
                    </div>

                    <div className="flex flex-col gap-1 min-w-0">
                        <span className="text-[10px] font-semibold tracking-wider uppercase text-slate-500 dark:text-slate-400">
                            R.U.T <span className="text-[#DC2626] font-bold">*</span>
                        </span>
                        <input
                            type="text"
                            value={newSubcontractor.rut}
                            onChange={handleRutChange}
                            placeholder="Ej: 76.543.210-K"
                            className="h-10 border border-[#E2E8F0] dark:border-slate-700 rounded-[10px] bg-white dark:bg-slate-800 px-3 text-[13px] text-[#0F172A] dark:text-slate-100 font-mono font-bold placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {!isRutEntered && (
                        <div className="flex gap-2 items-start bg-[#FEF3C7] dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/40 rounded-[10px] p-2 text-[12px] text-[#92400E] dark:text-amber-200 leading-snug">
                            <b className="text-[13px] shrink-0 font-extrabold">!</b>
                            <span>Ingrese el RUT para habilitar los demás campos.</span>
                        </div>
                    )}

                    {isRutFound && (
                        <div className="flex gap-2 items-center bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900/40 rounded-[10px] p-2 text-[12px] text-emerald-800 dark:text-emerald-200 leading-snug">
                            <span className="font-bold">✓</span>
                            <span>Empresa encontrada en el Maestro (Datos autocompletados)</span>
                        </div>
                    )}

                    <div className="flex flex-col gap-1 min-w-0">
                        <span className="text-[10px] font-semibold tracking-wider uppercase text-slate-500 dark:text-slate-400">
                            Razón social <span className="text-[#DC2626] font-bold">*</span>
                        </span>
                        <input
                            type="text"
                            disabled={!isRutEntered}
                            value={newSubcontractor.razonSocial}
                            onChange={(e) => setNewSubcontractor({ ...newSubcontractor, razonSocial: e.target.value })}
                            placeholder="Ej: Instalaciones SpA"
                            className="h-10 border border-[#E2E8F0] dark:border-slate-700 rounded-[10px] bg-white dark:bg-slate-800 px-3 text-[13px] text-[#0F172A] dark:text-slate-100 placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-[#F1F5F9] dark:disabled:bg-slate-800/40 disabled:text-[#94A3B8] disabled:cursor-not-allowed"
                        />
                    </div>

                    <div className="flex flex-col gap-1 min-w-0">
                        <span className="text-[10px] font-semibold tracking-wider uppercase text-slate-500 dark:text-slate-400">
                            Giro
                        </span>
                        <input
                            type="text"
                            disabled={!isRutEntered}
                            value={newSubcontractor.giro || ''}
                            onChange={(e) => setNewSubcontractor({ ...newSubcontractor, giro: e.target.value })}
                            placeholder="Ej: Obras civiles"
                            className="h-10 border border-[#E2E8F0] dark:border-slate-700 rounded-[10px] bg-white dark:bg-slate-800 px-3 text-[13px] text-[#0F172A] dark:text-slate-100 placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-[#F1F5F9] dark:disabled:bg-slate-800/40 disabled:text-[#94A3B8] disabled:cursor-not-allowed"
                        />
                    </div>
                </div>

                {/* TARJETA 2: CLASIFICACIÓN (09-crear-empresa-contratista.html) */}
                <div className="bg-white dark:bg-slate-900 rounded-[14px] p-3 shadow-xs border border-slate-100 dark:border-slate-800 flex flex-col gap-2.5">
                    <div className="text-[13px] font-bold text-[#0F172A] dark:text-slate-100">
                        Clasificación
                    </div>

                    <div className="flex flex-col gap-1 min-w-0">
                        <span className="text-[10px] font-semibold tracking-wider uppercase text-slate-500 dark:text-slate-400">
                            Tipo de subcontrato
                        </span>
                        <div className="relative">
                            <select
                                disabled={!isRutEntered}
                                value={newSubcontractor.subcontractTypeId || ''}
                                onChange={(e) => setNewSubcontractor({ ...newSubcontractor, subcontractTypeId: e.target.value })}
                                className="w-full h-10 border border-[#E2E8F0] dark:border-slate-700 rounded-[10px] bg-white dark:bg-slate-800 px-3 text-[13px] text-[#0F172A] dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-[#F1F5F9] dark:disabled:bg-slate-800/40 disabled:text-[#94A3B8] disabled:cursor-not-allowed appearance-none"
                            >
                                <option value="">Seleccionar tipo…</option>
                                {subcontractTypes.map(st => (
                                    <option key={st.id} value={st.id}>{st.name}</option>
                                ))}
                            </select>
                            <svg className="w-[18px] h-[18px] text-[#64748B] absolute right-3 top-3 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="m6 9 6 6 6-6" />
                            </svg>
                        </div>
                    </div>
                </div>

                {/* TARJETA 3: CONTACTO (09-crear-empresa-contratista.html) */}
                <div className="bg-white dark:bg-slate-900 rounded-[14px] p-3 shadow-xs border border-slate-100 dark:border-slate-800 flex flex-col gap-2.5">
                    <div className="text-[13px] font-bold text-[#0F172A] dark:text-slate-100">
                        Contacto
                    </div>

                    <div className="flex flex-col gap-1 min-w-0">
                        <span className="text-[10px] font-semibold tracking-wider uppercase text-slate-500 dark:text-slate-400">
                            Representante
                        </span>
                        <input
                            type="text"
                            disabled={!isRutEntered}
                            value={newSubcontractor.representante || ''}
                            onChange={(e) => setNewSubcontractor({ ...newSubcontractor, representante: e.target.value })}
                            placeholder="Nombre del representante"
                            className="h-10 border border-[#E2E8F0] dark:border-slate-700 rounded-[10px] bg-white dark:bg-slate-800 px-3 text-[13px] text-[#0F172A] dark:text-slate-100 placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-[#F1F5F9] dark:disabled:bg-slate-800/40 disabled:text-[#94A3B8] disabled:cursor-not-allowed"
                        />
                    </div>

                    <div className="flex flex-col gap-1 min-w-0">
                        <span className="text-[10px] font-semibold tracking-wider uppercase text-slate-500 dark:text-slate-400">
                            Teléfono
                        </span>
                        <input
                            type="text"
                            disabled={!isRutEntered}
                            value={newSubcontractor.telefono || ''}
                            onChange={(e) => setNewSubcontractor({ ...newSubcontractor, telefono: e.target.value })}
                            placeholder="+56 9 1234 5678"
                            className="h-10 border border-[#E2E8F0] dark:border-slate-700 rounded-[10px] bg-white dark:bg-slate-800 px-3 text-[13px] text-[#0F172A] dark:text-slate-100 placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-[#F1F5F9] dark:disabled:bg-slate-800/40 disabled:text-[#94A3B8] disabled:cursor-not-allowed"
                        />
                    </div>

                    <div className="flex flex-col gap-1 min-w-0">
                        <span className="text-[10px] font-semibold tracking-wider uppercase text-slate-500 dark:text-slate-400">
                            Correo electrónico
                        </span>
                        <input
                            type="email"
                            disabled={!isRutEntered}
                            value={newSubcontractor.correo || ''}
                            onChange={(e) => setNewSubcontractor({ ...newSubcontractor, correo: e.target.value })}
                            placeholder="contacto@empresa.cl"
                            className="h-10 border border-[#E2E8F0] dark:border-slate-700 rounded-[10px] bg-white dark:bg-slate-800 px-3 text-[13px] text-[#0F172A] dark:text-slate-100 placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-[#F1F5F9] dark:disabled:bg-slate-800/40 disabled:text-[#94A3B8] disabled:cursor-not-allowed"
                        />
                    </div>
                </div>
            </div>
        </KlFullModal>
    );
};
